import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Page,
  Card,
  FormLayout,
  TextField,
  Select,
  Tag,
  InlineStack,
  InlineGrid,
  RangeSlider,
  Checkbox,
  Button,
  BlockStack,
  Banner,
  List,
  Text,
} from "@shopify/polaris";
import { api, ApiRequestError } from "../api";
import posthog from "../posthog";
import type { Carrier, Rule, RuleConditions } from "../types";

const COUNTRY_OPTIONS = [
  { label: "Any country", value: "" },
  { label: "United States", value: "US" },
  { label: "Canada", value: "CA" },
  { label: "United Kingdom", value: "GB" },
  { label: "Australia", value: "AU" },
  { label: "Germany", value: "DE" },
];

const MAX_WEIGHT_GRAMS = 20000;

interface DescribeRuleInput {
  name: string;
  priority: string;
  enabled: boolean;
  tags: string[];
  weightRange: [number, number];
  totalMin: string;
  totalMax: string;
  destinationCountry: string;
  skuContains: string;
  preferredCarrier: string;
  maxDeliveryDays: string;
  carriers: Carrier[];
}

// Reads back the rule's current field values as a numbered explanation so the
// merchant sees exactly what will happen at match time without having to guess
// how each condition interacts. This is the shop's real behavior contract, not
// a marketing summary.
function describeRule(input: DescribeRuleInput): string[] {
  const lines: string[] = [];
  const conditions: string[] = [];
  if (input.tags.length > 0) conditions.push(`tag matches any of ${input.tags.join(", ")}`);
  const [wMin, wMax] = input.weightRange;
  if (wMin > 0 || wMax < MAX_WEIGHT_GRAMS) conditions.push(`weight is ${wMin}g to ${wMax}g`);
  const min = input.totalMin.trim();
  const max = input.totalMax.trim();
  if (min && max) conditions.push(`order total is $${min} to $${max}`);
  else if (min) conditions.push(`order total is at least $${min}`);
  else if (max) conditions.push(`order total is at most $${max}`);
  if (input.destinationCountry) {
    const country =
      COUNTRY_OPTIONS.find((c) => c.value === input.destinationCountry)?.label ??
      input.destinationCountry;
    conditions.push(`destination is ${country}`);
  }
  if (input.skuContains.trim()) conditions.push(`any line item SKU contains "${input.skuContains.trim()}"`);
  lines.push(conditions.length > 0 ? `Match orders where ${conditions.join(", and ")}.` : "Match every order (no conditions set).");

  const carrierLabel = input.preferredCarrier
    ? input.carriers.find((c) => c.code === input.preferredCarrier)?.label ?? input.preferredCarrier
    : null;
  lines.push(
    carrierLabel
      ? `Rate ${carrierLabel} only. If it returns no rates, fall back to the cheapest rate from any carrier.`
      : "Rate USPS, UPS, FedEx, and DHL together.",
  );

  const days = input.maxDeliveryDays.trim();
  if (days) {
    lines.push(`Filter out rates that miss a ${days}-day delivery target. An order's own deadline still wins.`);
  } else {
    lines.push("Filter out rates that miss the order's own promise deadline, if any.");
  }
  lines.push("Buy the cheapest rate that survives, and send the label to the default printer.");

  if (!input.enabled) {
    lines.push("This rule is currently disabled and will not run.");
  }
  return lines;
}

export default function RuleEditor(): JSX.Element {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id;

  const [name, setName] = useState("");
  const [priority, setPriority] = useState("100");
  const [enabled, setEnabled] = useState(true);

  const [tags, setTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState("");
  const [weightRange, setWeightRange] = useState<[number, number]>([0, MAX_WEIGHT_GRAMS]);
  const [totalMin, setTotalMin] = useState("");
  const [totalMax, setTotalMax] = useState("");
  const [destinationCountry, setDestinationCountry] = useState("");
  const [skuContains, setSkuContains] = useState("");

  const [carriers, setCarriers] = useState<Carrier[]>([]);
  // "" is the any-carrier option, sent to the API as null.
  const [preferredCarrier, setPreferredCarrier] = useState("");
  // "" means this rule sets no deadline, sent to the API as null. There is no
  // longer a shop-wide promise to inherit — an order with no deadline of its
  // own is rated on price alone.
  const [maxDeliveryDays, setMaxDeliveryDays] = useState("");

  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get<{ carriers: Carrier[] }>("/carriers").then(({ carriers: fetched }) => setCarriers(fetched));
  }, []);

  useEffect(() => {
    if (isNew) return;
    api.get<{ rule: Rule }>(`/rules/${id}`).then(({ rule }) => {
      setName(rule.name);
      setPriority(String(rule.priority));
      setEnabled(rule.enabled);
      setTags(rule.conditions.tag ?? []);
      if (rule.conditions.weight_range) {
        setWeightRange([rule.conditions.weight_range.min, rule.conditions.weight_range.max]);
      }
      if (rule.conditions.order_total_range) {
        setTotalMin(String(rule.conditions.order_total_range.min));
        setTotalMax(String(rule.conditions.order_total_range.max));
      }
      setDestinationCountry(rule.conditions.destination_country ?? "");
      setSkuContains(rule.conditions.sku_contains ?? "");
      setPreferredCarrier(rule.preferredCarrier ?? "");
      setMaxDeliveryDays(rule.maxDeliveryDays === null ? "" : String(rule.maxDeliveryDays));
    });
  }, [id, isNew]);

  function addTag(): void {
    const trimmed = tagInput.trim();
    if (trimmed && !tags.includes(trimmed)) {
      setTags([...tags, trimmed]);
    }
    setTagInput("");
  }

  function removeTag(tag: string): void {
    setTags(tags.filter((t) => t !== tag));
  }

  async function handleSave(): Promise<void> {
    setSaving(true);
    setError(null);

    const min = parseFloat(totalMin);
    const max = parseFloat(totalMax);

    const conditions: RuleConditions = {
      ...(tags.length > 0 && { tag: tags }),
      ...(weightRange[0] > 0 || weightRange[1] < MAX_WEIGHT_GRAMS
        ? { weight_range: { min: weightRange[0], max: weightRange[1] } }
        : {}),
      ...(min >= 0 && max > 0 ? { order_total_range: { min, max } } : {}),
      ...(destinationCountry && { destination_country: destinationCountry }),
      ...(skuContains && { sku_contains: skuContains }),
    };

    const payload = {
      name,
      priority: parseInt(priority, 10) || 100,
      enabled,
      conditions,
      preferred_carrier: preferredCarrier || null,
      max_delivery_days: maxDeliveryDays.trim() === "" ? null : parseInt(maxDeliveryDays, 10),
    };

    try {
      if (isNew) {
        await api.post("/rules", payload);
      } else {
        await api.put(`/rules/${id}`, payload);
      }
      posthog.capture("shipping_rule_saved", { action: isNew ? "created" : "updated", enabled });
      navigate("/rules");
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.body.message : "Could not save rule");
    } finally {
      setSaving(false);
    }
  }

  const carrierOptions = [
    { label: "Any carrier (cheapest overall)", value: "" },
    ...carriers.map((c) => ({ label: c.label, value: c.code })),
  ];

  return (
    <Page
      title={isNew ? "Create rule" : "Edit rule"}
      backAction={{ content: "Rules", onAction: () => navigate("/rules") }}
    >
      <BlockStack gap="400">
        {error && <Banner tone="critical">{error}</Banner>}

        <Card>
          <FormLayout>
            <Text as="h2" variant="headingMd">
              Rule details
            </Text>
            <TextField label="Rule name" value={name} onChange={setName} autoComplete="off" />
            <TextField
              label="Priority (lower runs first)"
              type="number"
              value={priority}
              onChange={setPriority}
              autoComplete="off"
            />
            <Checkbox label="Enabled" checked={enabled} onChange={setEnabled} />
          </FormLayout>
        </Card>

        <Card>
          <FormLayout>
            <Text as="h2" variant="headingMd">
              IF (conditions)
            </Text>
            <TextField
              label="Add tag"
              value={tagInput}
              onChange={setTagInput}
              autoComplete="off"
              onBlur={addTag}
              helpText="Press Tab or click away to add. Matches if order has ANY listed tag."
            />
            <InlineStack gap="100">
              {tags.map((tag) => (
                <Tag key={tag} onRemove={() => removeTag(tag)}>
                  {tag}
                </Tag>
              ))}
            </InlineStack>
            <RangeSlider
              label={`Order weight range: ${weightRange[0]}g - ${weightRange[1]}g`}
              value={weightRange}
              min={0}
              max={MAX_WEIGHT_GRAMS}
              step={50}
              onChange={(value) => setWeightRange(value as [number, number])}
            />
            <InlineGrid columns={{ xs: 1, sm: 2 }} gap="300">
              <TextField
                label="Order total minimum"
                type="number"
                value={totalMin}
                onChange={setTotalMin}
                autoComplete="off"
                helpText="Leave both blank to match any total"
              />
              <TextField
                label="Order total maximum"
                type="number"
                value={totalMax}
                onChange={setTotalMax}
                autoComplete="off"
              />
            </InlineGrid>
            <Select
              label="Destination country"
              options={COUNTRY_OPTIONS}
              value={destinationCountry}
              onChange={setDestinationCountry}
            />
            <TextField
              label="SKU contains (optional)"
              value={skuContains}
              onChange={setSkuContains}
              autoComplete="off"
            />
          </FormLayout>
        </Card>

        <Card>
          <FormLayout>
            <Text as="h2" variant="headingMd">
              THEN (action)
            </Text>
            <Select
              label="Carrier"
              options={carrierOptions}
              value={preferredCarrier}
              onChange={setPreferredCarrier}
              helpText="ShipPromise buys the cheapest rate EasyPost returns. Pick a carrier to narrow the search to that carrier's rates; if it returns none, the cheapest rate from any carrier is used instead."
            />
            <TextField
              label="Max delivery days (optional)"
              type="number"
              value={maxDeliveryDays}
              onChange={setMaxDeliveryDays}
              autoComplete="off"
              placeholder="No promise"
              // This said the opposite until it was checked against the cascade:
              // "a marketplace SLA or Shopify's own delivery date still wins over
              // this". Priority 1 in resolveDeliveryPromise is the rule, and it
              // returns immediately — above marketplace SLA (2) and Shopify native
              // (3). So the copy was inviting the one misconfiguration that costs
              // real money: a merchant setting 5 days here, believing a 2-day
              // Amazon Latest Delivery Date would still bind, gets the slower
              // service bought and takes the late-shipment hit.
              helpText="Buy the cheapest rate arriving within this many days, for orders this rule matches. Leave blank to rate on price alone. This overrides everything the order carries, including a marketplace SLA and Shopify's own delivery date — so leave it blank unless you mean to be stricter than the promise already on the order."
            />
          </FormLayout>
        </Card>

        <Card>
          <BlockStack gap="200">
            <Text as="h2" variant="headingMd">
              How this rule behaves
            </Text>
            <List type="number">
              {describeRule({
                name,
                priority,
                enabled,
                tags,
                weightRange,
                totalMin,
                totalMax,
                destinationCountry,
                skuContains,
                preferredCarrier,
                maxDeliveryDays,
                carriers,
              }).map((line) => (
                <List.Item key={line}>{line}</List.Item>
              ))}
            </List>
          </BlockStack>
        </Card>

        <InlineStack align="end">
          <Button variant="primary" onClick={handleSave} loading={saving} disabled={!name}>
            Save rule
          </Button>
        </InlineStack>
      </BlockStack>
    </Page>
  );
}
